import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { DealExitTypeEnumType } from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC, useRef, useState } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogTitle } from 'primitives/Dialog';
import LoadingIndicator from 'primitives/LoadingIndicator';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import dealExitTypeToReadable from 'utils/enums/deal-exit-type-to-readable';
import statusEnumToReadable from 'utils/enums/status-enum-to-readable';

const DEAL_DATA_FOR_EXIT_QUERY = gql(`
  query fetchDealDataForExit($companyId: ID!) {
    company(id: $companyId) {
      deals {
        id
        schemeName
        status
        finalisedAt
      }
    }
  }
`);

const CREATE_DEAL_EXIT_MUTATION = gql(`
  mutation CreateDealExit(
    $dealId: ID!
    $exitPricePerShare: Float!
    $exitedAt: String!
    $percentageOfSharesToExit: Float!
    $type: DealExitTypeEnumType!
    $comments: String
    $distributionExpenses: Float
    $multipleOnInvestment: Float!
    $totalSaleProceeds: Float
    $footnotes: String
    $distributionStatementFootnotes: [String!]
    $customDistributionStatementFootnotes: String
  ) {
    createDealExit(
      dealId: $dealId
      exitPricePerShare: $exitPricePerShare
      exitedAt: $exitedAt
      percentageOfSharesToExit: $percentageOfSharesToExit
      type: $type
      comments: $comments
      distributionExpenses: $distributionExpenses
      multipleOnInvestment: $multipleOnInvestment
      totalSaleProceeds: $totalSaleProceeds
      footnotes: $footnotes
      distributionStatementFootnotes: $distributionStatementFootnotes
      customDistributionStatementFootnotes: $customDistributionStatementFootnotes
    ) {
      id
      type
      createdAt
      updatedAt
      totalSaleProceeds
      footnotes
      distributionExpenses
      multipleOnInvestment
      pricePerShare
      exitedAt
      percentageOfShares
      comments
      distributionStatementFootnotes {
        selectedFootnotes
        customFootnotes
      }
    }
  }
`);

const CreateDealExitDialogButton: FC<{ companyId: string }> = ({ companyId }) => {
  const [showDialog, toggleDialog] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { loading, error, data, refetch } = useQuery(DEAL_DATA_FOR_EXIT_QUERY, {
    variables: {
      companyId,
    },
  });

  const [createDealExit, { loading: exitLoading, error: exitError }] = useMutation(
    CREATE_DEAL_EXIT_MUTATION,
    {
      refetchQueries: ['fetchDealExitsByCompany'],
    }
  );

  function renderContent() {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    return (
      <DialogPanel className="min-w-[60vw] min-h-[60vh]">
        <DialogTitle>Initiate Exit</DialogTitle>
        <FormPanel
          loading={exitLoading}
          error={exitError}
          onCancel={() => toggleDialog(false)}
          onSubmit={data => {
            createDealExit({
              variables: {
                dealId: data.dealId,
                exitPricePerShare: data.exitPricePerShare,
                exitedAt: data.exitedAt,
                percentageOfSharesToExit: data.percentageOfSharesToExit,
                type: data.type,
                comments: data.comments,
                distributionExpenses: data.distributionExpenses
                  ? parseFloat(data.distributionExpenses)
                  : null,
                multipleOnInvestment: data.multipleOnInvestment,
                totalSaleProceeds: data.totalSaleProceeds
                  ? parseFloat(data.totalSaleProceeds)
                  : null,
                footnotes: data.footnotes,
                distributionStatementFootnotes: data.distributionStatementFootnotes || [], // Handle new field
                customDistributionStatementFootnotes:
                  data.customDistributionStatementFootnotes || '', // Handle custom footnotes
              },
            }).then(() => toggleDialog(false));
          }}
          buttonRef={buttonRef}
        >
          <FormInput
            type="select"
            fieldName="dealId"
            label="Deal"
            defaultValue={''}
            options={data.company.deals.map(deal => ({
              label: `${deal.schemeName || ''} ${
                deal.finalisedAt
                  ? `(${statusEnumToReadable(deal.status).label}, Finalised: ${dayjs(
                      deal.finalisedAt
                    ).format('DD/MM/YYYY')}) `
                  : `(${statusEnumToReadable(deal.status).label}) `
              }`,
              value: deal.id,
            }))}
            validators={{ required: true }}
          />
          <FormInput
            type="select"
            fieldName="type"
            label="Type"
            defaultValue={DealExitTypeEnumType.SecondarySale}
            options={[
              DealExitTypeEnumType.SecondarySale,
              DealExitTypeEnumType.ShareSwap,
              DealExitTypeEnumType.LiquidationProceeds,
              DealExitTypeEnumType.BuybackProceeds,
              DealExitTypeEnumType.ReturnOfCapital,
              DealExitTypeEnumType.InterestIncome,
              DealExitTypeEnumType.DividendIncome,
              DealExitTypeEnumType.IncomeFromOtherSources,
              DealExitTypeEnumType.BonusShares,
              // Showing new Types only
            ].map(type => ({
              label: dealExitTypeToReadable(type).label,
              value: type,
            }))}
            validators={{ required: true }}
          />
          <FormInput
            type="currency"
            fieldName="exitPricePerShare"
            label="Liquidation Price Per Share"
            defaultValue=""
            validators={{ required: true }}
          />
          <FormInput
            type="date"
            fieldName="exitedAt"
            label="Liquidation Date"
            defaultValue=""
            validators={{ required: true }}
          />
          <FormInput
            type="number"
            fieldName="percentageOfSharesToExit"
            label="Percentage of Shares to Liquidate"
            defaultValue={100}
            validators={{ required: true }}
          />
          <FormInput type="textarea" fieldName="comments" label="Comments" defaultValue="" />
          <FormInput
            type="number"
            fieldName="distributionExpenses"
            label="Distribution Expenses"
            defaultValue=""
          />
          <FormInput
            type="number"
            fieldName="multipleOnInvestment"
            label="Multiple on Investment"
            defaultValue=""
            validators={{ required: true }}
          />
          <FormInput
            type="currency"
            fieldName="totalSaleProceeds"
            label="Total Sale Proceeds"
            helperText="Total amount received from the sale"
            defaultValue=""
          />
          <FormInput
            type="multi_select"
            fieldName="distributionStatementFootnotes"
            label="Distribution Statement Footnotes"
            defaultValue={[]}
            options={[
              {
                label: 'Tax Deducted at Source - 10%',
                value:
                  'Tax has been withheld at the rate of 10 percent in case of resident investors and at the rates in force for the non-resident investors, as per the prevailing Indian income-tax provisions.',
              },
              {
                label: 'Distribution(s) may be recalled',
                value:
                  'Distribution(s) may be recalled by AngelList India, if necessary, to satisfy any obligations of the Fund including indemnification, tax or otherwise in relation to this scheme.',
              },
              {
                label: 'Buyback Proceeds (Profitable)',
                value:
                  'Please note that as per section 2(22)(f) of the Income-tax Act, 1961, proceeds from the buyback will be treated as deemed dividends and taxed accordingly at the applicable rates. Furthermore, investors may claim the cost of acquisition for the bought-back securities as a capital loss in their tax returns.',
              },
              {
                label: 'Buyback Loss',
                value:
                  'The cost of acquisition for the securities being bought back shall be claimed as a capital loss in your tax return.',
              },
              {
                label: 'Bonus Shares',
                value: ' The cost of acquisition is nil for the bonus shares.',
              },
            ]}
            validators={{ required: true }}
          />
          <p className="flex items-center col-span-full">
            <a
              href="https://docs.google.com/spreadsheets/d/1henWQAW_9Xjj2xV-3YRgCiyjo28IhGR4/edit?gid=492949198#gid=492949198"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 hover:text-blue-600"
            >
              Footnotes Reference Sheet
            </a>{' '}
          </p>
          <FormInput
            type="textarea"
            fieldName="customDistributionStatementFootnotes"
            label="Custom Distribution Statement Footnote"
            defaultValue=""
          />
          <FormInput
            type="textarea"
            fieldName="footnotes"
            label="Unit Statement Footnote"
            defaultValue=""
          />
        </FormPanel>
        <DialogActions>
          <Button
            variant="secondary"
            loading={exitLoading}
            onClick={() => {
              // @ts-ignore
              buttonRef.current ? buttonRef.current.cancel() : toggleDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            loading={exitLoading}
            onClick={() => {
              // @ts-ignore
              buttonRef.current?.submit();
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </DialogPanel>
    );
  }

  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Initiate Exit</Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)}>
        {renderContent()}
      </Dialog>
    </>
  );
};

export default CreateDealExitDialogButton;
